/**
 * GA4 + Google Ads Tracking Utility
 * 
 * Centralized event tracking for Google Analytics 4 and Google Ads.
 * All events follow the dataLayer pattern for GTM compatibility.
 * 
 * SETUP: Replace GA4_MEASUREMENT_ID with your actual G-XXXXXXXXXX ID.
 */

// ── IDs ──────────────────────────────────────────────────
export const GA4_MEASUREMENT_ID = 'G-SR985L6S05';
export const GADS_ID = 'AW-17934526610';
export const GADS_CONVERSION_LABEL = 'AW-17934526610/C4IZCKu3uPMbEJLR7OdC';

// ── Types ────────────────────────────────────────────────
type EventCategory = 'conversion' | 'engagement';

interface GA4EventParams {
  event_category: EventCategory;
  page_path: string;
  page_title: string;
  device_type: string;
  traffic_source: string;
  [key: string]: string | number | boolean | undefined;
}

// ── Helpers ──────────────────────────────────────────────
function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getTrafficSource(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('utm_source') || params.get('gclid') ? 'google_ads' : document.referrer ? new URL(document.referrer).hostname : 'direct';
}

function gtag(command: string, ...args: unknown[]) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(command, ...args);
  }
}

// Dedup guard: tracks fired events per session to prevent duplicates
const firedEvents = new Set<string>();

function dedupeKey(eventName: string, extra?: string): string {
  return `${eventName}__${extra || 'default'}`;
}

// ── Debug mode ───────────────────────────────────────────
const isDebugMode = (): boolean =>
  import.meta.env.DEV || localStorage.getItem('ga4_debug') === 'true';

// ── Core send ────────────────────────────────────────────
export function sendGA4Event(
  eventName: string,
  category: EventCategory,
  extra: Record<string, string | number | boolean | undefined> = {},
  { allowDuplicate = false, dedupeId }: { allowDuplicate?: boolean; dedupeId?: string } = {},
) {
  const key = dedupeKey(eventName, dedupeId);
  if (!allowDuplicate && firedEvents.has(key)) return;
  firedEvents.add(key);

  const debug = isDebugMode();

  const params: GA4EventParams = {
    event_category: category,
    page_path: window.location.pathname,
    page_title: document.title,
    device_type: getDeviceType(),
    traffic_source: getTrafficSource(),
    ...(debug ? { debug_mode: true } : {}),
    ...extra,
  };

  if (debug) {
    console.log(`[GA4 🔍] ${eventName}`, params);
  }

  // Push to dataLayer for GTM compatibility
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...params });

  // Fire directly via gtag — debug_mode hardcoded for DebugView
  gtag('event', eventName, { ...params, debug_mode: true });
}

// ── CONVERSÕES PRINCIPAIS ────────────────────────────────
export function trackLeadDiagnosticoStart() {
  sendGA4Event('lead_diagnostico_start', 'conversion', {
    event_label: 'CTA Diagnóstico Gratuito',
  });
}

export function trackLeadFormSubmit(formName?: string) {
  sendGA4Event('lead_form_submit', 'conversion', {
    form_name: formName || 'diagnostico',
  });
}

export function trackSignupStart() {
  sendGA4Event('signup_start', 'conversion');
}

export function trackSignupComplete() {
  sendGA4Event('signup_complete', 'conversion');
}

export function trackTrialStart() {
  sendGA4Event('trial_start', 'conversion');
}

// ── MICRO-CONVERSÕES ─────────────────────────────────────
export function trackCTAPrimaryClick(label: string) {
  sendGA4Event('cta_primary_click', 'engagement', {
    event_label: label,
  }, { allowDuplicate: true });
}

export function trackWhatsAppClick() {
  sendGA4Event('whatsapp_click', 'engagement');
}

export function trackScroll75() {
  sendGA4Event('scroll_75', 'engagement');
}

export function trackPricingView() {
  sendGA4Event('pricing_view', 'engagement');
}

export function trackFeatureExpand(featureName: string) {
  sendGA4Event('feature_expand', 'engagement', {
    feature_name: featureName,
  }, { allowDuplicate: true });
}

// ── Google Ads Conversion (existing) ─────────────────────
export function fireGoogleAdsConversion() {
  gtag('event', 'conversion', {
    send_to: GADS_CONVERSION_LABEL,
    value: 1.0,
    currency: 'BRL',
  });
}
